import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { codeApi, CodeListItem } from '../api/codeApi';

import { useDebounce } from '../hooks/useDebounce';
import PlansModal from '../components/common/PlansModal';
import toast from 'react-hot-toast';
import { subscriptionApi, CurrentSubDto, SubCheckResponse } from '../api/subscriptionApi';

//const SUBS_URL = 'http://localhost:5002/api';
const SUBS_URL = process.env.REACT_APP_SUBS_URL || 'http://localhost:5002/api';

const CODE_TYPE_LABELS: Record<string, string> = {
    ICD10: 'ICD-10-CM',
    ICD10PCS: 'ICD-10-PCS',
    HCPCS: 'HCPCS',
    NDC: 'NDC',
    LOINC: 'LOINC',
    RXNORM: 'RxNorm',
};

const CODE_TYPE_COLORS: Record<string, { badge: string; dot: string; hover: string }> = {
    ICD10:    { badge: 'bg-teal-50 text-teal-700',     dot: 'bg-teal-500',   hover: 'hover:border-teal-300 hover:bg-teal-50/40' },
    ICD10PCS: { badge: 'bg-blue-50 text-blue-700',     dot: 'bg-blue-500',   hover: 'hover:border-blue-300 hover:bg-blue-50/40' },
    HCPCS:    { badge: 'bg-violet-50 text-violet-700', dot: 'bg-violet-500', hover: 'hover:border-violet-300 hover:bg-violet-50/40' },
    NDC:      { badge: 'bg-amber-50 text-amber-700',   dot: 'bg-amber-500',  hover: 'hover:border-amber-300 hover:bg-amber-50/40' },
    LOINC:    { badge: 'bg-green-50 text-green-700',   dot: 'bg-green-500',  hover: 'hover:border-green-300 hover:bg-green-50/40' },
    RXNORM:   { badge: 'bg-indigo-50 text-indigo-700', dot: 'bg-indigo-500', hover: 'hover:border-indigo-300 hover:bg-indigo-50/40' },
};

// ─── Search Counter ──────────────────────────────────────────────────────────
const SearchCounter = ({ sub, guestCount }: { sub: CurrentSubDto | null; guestCount: SubCheckResponse | null }) => {
    const token = localStorage.getItem('sub_token');
    if (token && sub) {
        if (sub.searchLimit === -1) return (
            <div className="flex items-center gap-1.5 mt-2 px-1">
                <svg className="text-teal-500" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                <span className="text-[11px] text-teal-600 font-semibold">Unlimited searches — {sub.planName} Plan</span>
            </div>
        );
        const pct = Math.min(100, Math.round((sub.searchesUsed / sub.searchLimit) * 100));
        const isNear = pct >= 80;
        const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-400' : 'bg-teal-500';
        return (
            <div className="mt-2 px-1">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                        <svg className="text-gray-400" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                        <span className="text-[11px] text-gray-500">
                            <span className={`font-bold ${isNear ? 'text-amber-600' : 'text-gray-700'}`}>{sub.searchesUsed}</span>
                            {' '}of{' '}
                            <span className="font-bold text-gray-700">{sub.searchLimit.toLocaleString()}</span>
                            {' '}searches used{sub.searchLimit <= 2 ? ' today' : ' this month'}
                        </span>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isNear ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-500'}`}>{sub.planName}</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }}/>
                </div>
            </div>
        );
    }
    if (!token && guestCount) {
        const used = guestCount.searchesUsed;
        const limit = guestCount.searchLimit;
        const pct = Math.min(100, Math.round((used / limit) * 100));
        const barColor = pct >= 100 ? 'bg-red-500' : pct >= 50 ? 'bg-amber-400' : 'bg-teal-500';
        return (
            <div className="mt-2 px-1">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                        <svg className="text-gray-400" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                        <span className="text-[11px] text-gray-500">
                            <span className={`font-bold ${pct >= 100 ? 'text-red-600' : 'text-gray-700'}`}>{used}</span>
                            {' '}of{' '}
                            <span className="font-bold text-gray-700">{limit}</span>
                            {' '}free searches used today
                        </span>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">Guest</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }}/>
                </div>
            </div>
        );
    }
    return null;
};

// ─── Guest Limit Modal ────────────────────────────────────────────────────
const GuestLimitModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const navigate = useNavigate();
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
                <div className="w-14 h-14 bg-amber-50 border border-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="text-amber-500" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Daily Limit Reached</h2>
                <p className="text-sm text-gray-500 leading-relaxed mb-6">
                    You've used your <strong>2 free searches</strong> for today.
                    Login to continue searching codes.
                </p>
                <div className="space-y-2.5">
                    <button onClick={() => { navigate('/login'); onClose(); }}
                        className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                            <polyline points="10 17 15 12 10 7"/>
                            <line x1="15" y1="12" x2="3" y2="12"/>
                        </svg>
                        Login to Your Account
                    </button>
                    <button onClick={() => { navigate('/register'); onClose(); }}
                        className="w-full py-3 border border-teal-200 text-teal-700 text-sm font-bold rounded-xl hover:bg-teal-50 transition-all">
                        Create Free Account
                    </button>
                    <button onClick={onClose} className="w-full py-2 text-sm text-gray-400 hover:text-gray-600">Dismiss</button>
                </div>
                <p className="text-[10px] text-gray-300 mt-4">Free accounts get 2 searches/day · Upgrade for more</p>
            </div>
        </div>
    );
};

// ─── Skeleton Card ────────────────────────────────────────────────────────
const SkeletonCard = () => (
    <div className="bg-white border border-gray-100 rounded-xl p-4 animate-pulse">
        <div className="flex items-center justify-between mb-3">
            <div className="h-5 w-16 bg-gray-100 rounded-lg"/>
            <div className="h-3 w-3 bg-gray-100 rounded-full"/>
        </div>
        <div className="h-3.5 bg-gray-100 rounded w-full mb-2"/>
        <div className="h-3 bg-gray-50 rounded w-3/4"/>
    </div>
);

// ─── Code Card ────────────────────────────────────────────────────────────
const CodeCard = ({ item, codeType }: { item: CodeListItem; codeType: string }) => {
    const navigate = useNavigate();
    const colors = CODE_TYPE_COLORS[codeType] || { badge: 'bg-gray-50 text-gray-700', dot: 'bg-gray-400', hover: 'hover:border-gray-300' };
    return (
        <button
            onClick={() => navigate(`/code/${item.codeType || codeType}/${item.code}`)}
            className={`bg-white border border-gray-100 rounded-xl p-4 text-left transition-all duration-200 group w-full shadow-sm hover:shadow-md ${colors.hover}`}
        >
            <div className="flex items-start justify-between mb-2.5">
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-lg font-mono ${colors.badge}`}>
                    {item.code}
                </span>
                <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${colors.dot}`}/>
            </div>
            <p className="text-[12px] text-gray-700 leading-relaxed line-clamp-2 group-hover:text-gray-900 transition-colors">
                {item.description}
            </p>
            {item.category && (
                <p className="text-[10px] text-gray-400 mt-2 truncate">{item.category}</p>
            )}
        </button>
    );
};

// ─── Scroll To Top Button ─────────────────────────────────────────────────
const ScrollTopButton = () => {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = document.getElementById('main-scroll');
        if (!el) return;
        const onScroll = () => setVisible(el.scrollTop > 300);
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);
    const scrollToTop = () => {
        document.getElementById('main-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
    };
    if (!visible) return null;
    return (
        <button onClick={scrollToTop} title="Back to top"
            className="fixed bottom-8 right-6 z-50 w-11 h-11 bg-teal-600 hover:bg-teal-700 active:scale-95 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 15l-6-6-6 6"/>
            </svg>
        </button>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────
const CodeTypeListPage = () => {
    const { codeType = 'ICD10' } = useParams<{ codeType: string }>();
    const navigate = useNavigate();
    const typeKey = codeType.toUpperCase().replace(/-/g, '');
    const label = CODE_TYPE_LABELS[typeKey] || codeType;

    const [searchInput, setSearchInput] = useState('');
    const [codes, setCodes] = useState<CodeListItem[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [suggestions, setSuggestions] = useState<CodeListItem[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSearch, setActiveSearch] = useState<string | null>(null);

    // Counter
    const [currentSub, setCurrentSub] = useState<CurrentSubDto | null>(null);
    const [guestCount, setGuestCount] = useState<SubCheckResponse | null>(null);

    // Modals
    const [guestModalOpen, setGuestModalOpen] = useState(false);
    const [plansOpen, setPlansOpen] = useState(false);
    const [planMessage, setPlanMessage] = useState('');

    const observerRef = useRef<HTMLDivElement>(null);
    const searchWrapperRef = useRef<HTMLDivElement>(null);
    // Suggestions use searchInput directly — debouncedSearch not needed for search trigger

    // ✅ Outside click se suggestions band karo (onBlur+timeout ki jagah — woh click se race hota tha)
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load counter
    useEffect(() => {
        const loadCounter = async () => {
            const token = localStorage.getItem('sub_token');
            if (token) {
                const sub = await subscriptionApi.getCurrentSubscription();
                setCurrentSub(sub);
            } else {
                try { const g = await subscriptionApi.getGuestCount(); setGuestCount(g); } catch {}
            }
        };
        loadCounter();
    }, []);

    // ✅ Subscription check — SubscriptionAPI se, counter refresh karo baad mein
    const checkSubscription = useCallback(async (term: string): Promise<boolean> => {
        const token = localStorage.getItem('sub_token');
        try {
            if (!token) {
                const res = await fetch(`${SUBS_URL}/subscription/guest-check`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });
                const data = await res.json();
                if (!data.allowed || res.status === 402) {
                    setGuestModalOpen(true);
                    setGuestCount(data);
                    return false;
                }
                setGuestCount(data);
                return true;
            } else {
                const res = await fetch(`${SUBS_URL}/subscription/check`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ searchTerm: term })
                });
                const data = await res.json();
                if (!data.allowed || res.status === 402) {
                    setPlanMessage(data.message || 'Upgrade your plan to search more codes.');
                    setPlansOpen(true);
                    // Refresh sub counter
                    subscriptionApi.getCurrentSubscription().then(s => setCurrentSub(s));
                    return false;
                }
                // Refresh counter after allowed
                subscriptionApi.getCurrentSubscription().then(s => setCurrentSub(s));
                return true;
            }
        } catch { return true; }
    }, []);

    const abortRef = useRef<AbortController | null>(null);

    const loadCodes = useCallback(async (p: number, search: string, reset = false) => {
        // Cancel previous request
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        if (reset) setInitialLoading(true);
        else setLoading(true);
        try {
            // Search me 20, browse me 50
            const pageSize = search ? 20 : 50;
            const data = await codeApi.listCodes(typeKey, p, pageSize, search);
            setCodes(prev => reset ? (data.codes ?? []) : [...prev, ...(data.codes ?? [])]);
            setHasMore(data.hasMore ?? false);
            setTotal(data.total ?? 0);
            setPage(p);
        } catch (err: any) {
            if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
            if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
                toast.error('Request timed out. Check if backend is running on port 5001.');
            }
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    }, [typeKey]);

    // ✅ Reset search when code type changes
    useEffect(() => {
        setSearchInput('');
        setActiveSearch(null);
        setSuggestions([]);
        setCodes([]);
        setPage(1);
        setHasMore(true);
        loadCodes(1, '', true);
    }, [typeKey]);

    // ✅ When activeSearch changes (after subscription verified) — load results
    useEffect(() => {
        if (activeSearch !== null) {
            setCodes([]);
            setPage(1);
            setHasMore(true);
            loadCodes(1, activeSearch, true);
        }
    }, [activeSearch]);

    // ✅ Trigger search — Enter key or suggestion click
    const triggerSearch = useCallback(async (term: string) => {
        if (!term.trim()) {
            setActiveSearch(null);
            setSearchInput('');
            return;
        }
        const allowed = await checkSubscription(term.trim());
        if (allowed) setActiveSearch(term.trim());
        else setSearchInput(''); // clear input if blocked
    }, [checkSubscription]);

    // ✅ Suggestions — usi code type ki table se (fast, 6 items)
    useEffect(() => {
        if (searchInput.length < 2) { setSuggestions([]); return; }
        const timer = setTimeout(() => {
            codeApi.listCodes(typeKey, 1, 6, searchInput)
                .then(data => setSuggestions((data.codes ?? []).slice(0, 6)))
                .catch(() => setSuggestions([]));
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput, typeKey]);

    // Infinite scroll
    useEffect(() => {
        const el = observerRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasMore && !loading && !initialLoading)
                    loadCodes(page + 1, activeSearch ?? '');
            },
            { threshold: 0.1 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMore, loading, initialLoading, page, activeSearch, loadCodes]);

    return (
        <div className="bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-100">
                <div className="max-w-screen-xl mx-auto px-4 py-6">
                    <div className="flex items-center gap-2 text-[11px] text-gray-400 mb-3">
                        <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="m9 18 6-6-6-6"/></svg>
                        <span className="text-gray-600 font-semibold">{label}</span>
                    </div>

                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{label} Codes</h1>
                            <p className="text-sm text-gray-400 mt-1">
                                {total > 0
                                    ? <span><span className="font-bold text-gray-600 font-mono">{total.toLocaleString()}</span> codes available</span>
                                    : 'Loading...'}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            {Object.entries(CODE_TYPE_LABELS).filter(([k]) => k !== typeKey).slice(0, 3).map(([k, l]) => (
                                <button key={k} onClick={() => navigate(`/codes/${k}`)}
                                    className="text-[11px] font-semibold px-3 py-1.5 border border-gray-200 rounded-full text-gray-500 hover:border-teal-300 hover:text-teal-600 transition-all hidden md:block">
                                    {l}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Search */}
                    <div className="relative" ref={searchWrapperRef}>
                        <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus-within:border-teal-400 focus-within:bg-white transition-all">
                            <svg className="text-gray-400 flex-shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={e => { setSearchInput(e.target.value); setShowSuggestions(true); }}
                                onKeyDown={e => { if (e.key === 'Enter') { setShowSuggestions(false); triggerSearch(searchInput); } }}
                                onFocus={() => setShowSuggestions(true)}
                                placeholder={`Search ${label} codes or conditions...`}
                                className="flex-1 ml-3 bg-transparent text-sm text-gray-800 placeholder-gray-400 focus:outline-none"
                            />
                            {searchInput && (
                                <button onClick={() => setSearchInput('')} className="text-gray-400 hover:text-gray-600 transition-colors">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <path d="M18 6 6 18M6 6l12 12"/>
                                    </svg>
                                </button>
                            )}
                        </div>
                        {/* ✅ Counter bar */}
                    <SearchCounter sub={currentSub} guestCount={guestCount} />

                    {/* Suggestions */}
                        {showSuggestions && suggestions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-lg z-30 overflow-hidden">
                                {suggestions.map((item, i) => (
                                    <button key={i} onClick={() => { setShowSuggestions(false); setSearchInput(''); navigate(`/code/${item.codeType || typeKey}/${item.code}`); }}
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors flex items-center gap-3">
                                        <svg className="text-gray-300 flex-shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                                        </svg>
                                        <span dangerouslySetInnerHTML={{
                                            __html: item.description.replace(new RegExp(`(${searchInput})`, 'gi'), '<strong class="text-teal-600">$1</strong>')
                                        }}/>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Cards */}
            <div className="max-w-screen-xl mx-auto px-4 py-6">
                {activeSearch && !initialLoading && (
                    <p className="text-[12px] text-gray-500 mb-4">
                        <span className="font-bold text-gray-700">{total.toLocaleString()}</span> results for "{activeSearch}"
                    </p>
                )}

                {initialLoading && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {Array.from({ length: 20 }).map((_, i) => <SkeletonCard key={i}/>)}
                    </div>
                )}

                {!initialLoading && codes.length === 0 && (
                    <div className="text-center py-20">
                        <div className="w-14 h-14 bg-gray-50 border border-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="text-gray-300" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                            </svg>
                        </div>
                        <p className="text-sm font-semibold text-gray-600 mb-1">No results found</p>
                        <p className="text-xs text-gray-400">Try different keywords</p>
                    </div>
                )}

                {!initialLoading && codes.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {codes.map((item, i) => (
                            <CodeCard key={`${item.code}-${i}`} item={item} codeType={typeKey}/>
                        ))}
                    </div>
                )}

                <div ref={observerRef} className="h-10 mt-4 flex items-center justify-center">
                    {loading && !initialLoading && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                            <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"/>
                            Loading more...
                        </div>
                    )}
                </div>
            </div>

            {/* Scroll to top */}
            <ScrollTopButton/>

            {/* Modals */}
            <GuestLimitModal isOpen={guestModalOpen} onClose={() => setGuestModalOpen(false)}/>
            <PlansModal isOpen={plansOpen} onClose={() => setPlansOpen(false)} message={planMessage}/>
        </div>
    );
};

export default CodeTypeListPage;